import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createIntegrationConnectionsApi } from "@playbookai/api-clients";
import { useOauthCompletionWatcher } from "../oauth/use-oauth-completion-watcher.js";
import {
  navigateAuthorizationWindow,
  openAuthorizationWindow,
  type AuthorizationWindow,
} from "./oauth-window.js";

type ConnectionsApi = ReturnType<typeof createIntegrationConnectionsApi>;

/** Shared new-tab OAuth round trip used by Connect and Reconnect entry points. */
export function useConnectionAuthorization(
  api: ConnectionsApi,
  refresh: () => Promise<unknown>,
): {
  readonly start: (integrationId: string) => void;
  readonly canReopen: boolean;
  readonly reopen: () => void;
  readonly watching: boolean;
  readonly isPending: boolean;
  readonly error: unknown | null;
  readonly windowError: string | null;
} {
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);
  const [windowError, setWindowError] = useState<string | null>(null);
  const completion = useOauthCompletionWatcher(refresh);
  const connect = useMutation({
    mutationFn: (attempt: {
      integrationId: string;
      authorizationWindow: AuthorizationWindow;
    }) => api.startAuthorization(attempt.integrationId),
    onSuccess: (result, attempt) => {
      setAuthorizationUrl(result.authorizationUrl);
      void refresh();
      if (
        !navigateAuthorizationWindow(
          attempt.authorizationWindow,
          result.authorizationUrl,
        )
      ) {
        setWindowError(
          "The authorization window was closed. Click Connect to try again.",
        );
        return;
      }
      completion.watch(attempt.authorizationWindow);
    },
    onError: (_error, attempt) => {
      attempt.authorizationWindow.close();
    },
  });

  const start = (integrationId: string): void => {
    setWindowError(null);
    setAuthorizationUrl(null);
    const authorizationWindow = openAuthorizationWindow();
    if (authorizationWindow === null) {
      setWindowError(
        "Your browser blocked the authorization window. Allow pop-ups for this site and try again.",
      );
      return;
    }
    connect.mutate({ integrationId, authorizationWindow });
  };

  return {
    canReopen: authorizationUrl !== null,
    reopen: () => {
      const opened = openAuthorizationWindow();
      if (
        !opened ||
        !authorizationUrl ||
        !navigateAuthorizationWindow(opened, authorizationUrl)
      ) {
        setWindowError(
          "Allow pop-ups for this site to open the pending sign-in.",
        );
        return;
      }
      setWindowError(null);
      setAuthorizationUrl(null);
      completion.watch(opened);
    },
    start,
    watching: completion.watching,
    isPending: connect.isPending,
    error: connect.error,
    windowError,
  };
}
